import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Badge, Spinner } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, FormField, Label } from '../../components/ui/Input';
import { useToast } from '../../contexts/ToastContext';
import { kycApi, type KycStatusResponse } from '../../api/kyc';
import { ApiError } from '../../api/client';
import { fmtDate } from '../../lib/format';

// Matches the backend DocumentType enum (NATIONAL_ID, PAN, PASSPORT, DRIVING_LICENSE, AADHAAR, OTHER)
const DOC_TYPES = [
  { value: 'PASSPORT',         label: 'Passport' },
  { value: 'NATIONAL_ID',      label: 'National ID card' },
  { value: 'DRIVING_LICENSE',  label: "Driver's license" },
  { value: 'PAN',              label: 'PAN card (India)' },
  { value: 'AADHAAR',          label: 'Aadhaar (India)' },
  { value: 'OTHER',            label: 'Other' },
];

// Curated country list. Users from anywhere else can select "Other (specify)".
const COUNTRIES = [
  { code: 'IN', name: 'India' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'PT', name: 'Portugal' },
  { code: 'IE', name: 'Ireland' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SG', name: 'Singapore' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'TH', name: 'Thailand' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'PH', name: 'Philippines' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'KE', name: 'Kenya' },
  { code: 'EG', name: 'Egypt' },
  { code: 'TR', name: 'Turkey' },
  { code: 'IL', name: 'Israel' },
  { code: 'NZ', name: 'New Zealand' },
];

export default function KycSubmit() {
  const toast = useToast();
  const nav = useNavigate();

  const [statusData, setStatusData] = useState<KycStatusResponse | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  // Form state
  const [fullName, setFullName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [countryCode, setCountryCode] = useState('IN');
  const [countryOther, setCountryOther] = useState('');
  const [documentType, setDocumentType] = useState('PASSPORT');
  const [documentNumber, setDocumentNumber] = useState('');
  const [taxIdNumber, setTaxIdNumber] = useState('');
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [selfieFile, setSelfieFile] = useState<File | null>(null);
  const [taxDocFile, setTaxDocFile] = useState<File | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    kycApi
      .status()
      .then(setStatusData)
      .catch(() =>
        setStatusData({ status: 'NONE', approvedAt: null, canSubmit: true, submission: null })
      )
      .finally(() => setLoadingStatus(false));
  }, []);

  if (loadingStatus) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size={40} />
      </div>
    );
  }

  const status = statusData?.status ?? 'NONE';
  const isOtherCountry = countryCode === 'OTHER';

  // Quick client-side mobile sanity check (server enforces strict E.164)
  const isMobileShapeValid = (() => {
    const digits = mobileNumber.replace(/\D/g, '');
    return digits.length >= 7 && digits.length <= 15;
  })();

  // If already approved/pending, show status card instead of form
  if (status === 'PENDING' || status === 'APPROVED') {
    return (
      <div className="max-w-2xl mx-auto py-10 px-4">
        <div className="mb-6">
          <Link
            to="/dashboard"
            className="text-violet-600 hover:underline text-sm font-semibold"
          >
            ← Back to dashboard
          </Link>
        </div>
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h1 className="font-display text-3xl font-bold">KYC verification</h1>
            <Badge kind={status === 'APPROVED' ? 'success' : 'warn'}>{status}</Badge>
          </div>

          {status === 'PENDING' && (
            <p className="text-ink-700">
              Your KYC submission is under review. We typically respond within 1–3 business
              days. You'll receive an email when there's a decision.
            </p>
          )}
          {status === 'APPROVED' && (
            <p className="text-success">
              ✓ Your KYC has been approved. Commission payouts can now be processed to your
              linked wallet.
            </p>
          )}

          {statusData?.submission && (
            <div className="mt-6 bg-paper-100 p-4 rounded-2xl space-y-2 text-sm">
              <div>
                <span className="text-ink-600">Submitted:</span>{' '}
                <span className="font-mono">{fmtDate(statusData.submission.submittedAt)}</span>
              </div>
              {statusData.submission.decidedAt && (
                <div>
                  <span className="text-ink-600">Decided:</span>{' '}
                  <span className="font-mono">{fmtDate(statusData.submission.decidedAt)}</span>
                </div>
              )}
              {statusData.submission.fullName && (
                <div>
                  <span className="text-ink-600">Name:</span>{' '}
                  <span className="font-mono">{statusData.submission.fullName}</span>
                </div>
              )}
              {statusData.submission.mobileNumber && (
                <div>
                  <span className="text-ink-600">Mobile:</span>{' '}
                  <span className="font-mono">{statusData.submission.mobileNumber}</span>
                </div>
              )}
              <div>
                <span className="text-ink-600">Document:</span>{' '}
                <span className="font-mono">{statusData.submission.documentType}</span>
              </div>
              <div>
                <span className="text-ink-600">Country:</span>{' '}
                <span className="font-mono">
                  {statusData.submission.countryCode === 'OTHER'
                    ? statusData.submission.countryOther ?? 'Other'
                    : statusData.submission.countryCode}
                </span>
              </div>
              {statusData.submission.hasTaxId && (
                <div>
                  <span className="text-ink-600">Tax ID:</span>{' '}
                  <span className="font-mono">On file (encrypted)</span>
                </div>
              )}
              {statusData.submission.hasTaxDocument && (
                <div>
                  <span className="text-ink-600">Tax document:</span>{' '}
                  <span className="font-mono">Uploaded</span>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    );
  }

  // REJECTED users see prior decision + can resubmit
  const isResubmit = status === 'REJECTED';

  // Pull the rejection reason from whichever field the backend populated.
  // Newer backend stores it in `rejectionReason`; older one used `adminNotes`.
  const previousRejectionReason =
    (statusData?.submission as { rejectionReason?: string | null } | undefined)
      ?.rejectionReason ??
    statusData?.submission?.adminNotes ??
    null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isMobileShapeValid) {
      setError('Mobile number must be 7–15 digits including country code, e.g. +91 98765 43210');
      return;
    }
    if (!documentFile || !selfieFile) {
      setError('Please attach both an ID image and a selfie');
      return;
    }
    if (documentFile.size > 5 * 1024 * 1024 || selfieFile.size > 5 * 1024 * 1024) {
      setError('Each image must be under 5 MB');
      return;
    }
    if (taxDocFile && taxDocFile.size > 5 * 1024 * 1024) {
      setError('Tax document must be under 5 MB');
      return;
    }
    if (isOtherCountry && countryOther.trim().length < 2) {
      setError('Please specify your country');
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('fullName', fullName);
      fd.append('dateOfBirth', dateOfBirth);
      fd.append('mobileNumber', mobileNumber);
      fd.append('countryCode', countryCode);
      if (isOtherCountry) {
        fd.append('countryOther', countryOther.trim());
      }
      fd.append('documentType', documentType);
      if (documentNumber.trim()) {
        fd.append('documentNumber', documentNumber.trim());
      }
      if (taxIdNumber.trim()) {
        fd.append('taxIdNumber', taxIdNumber.trim());
      }
      // Field names match the backend multer config: document, selfie, taxDocument
      fd.append('document', documentFile);
      fd.append('selfie', selfieFile);
      if (taxDocFile) {
        fd.append('taxDocument', taxDocFile);
      }

      await kycApi.submit(fd);
      toast.success('KYC submitted. You will hear back within 1–3 business days.');
      nav('/dashboard');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Submission failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-10 px-4">
      <div className="mb-6">
        <Link
          to="/dashboard"
          className="text-violet-600 hover:underline text-sm font-semibold"
        >
          ← Back to dashboard
        </Link>
      </div>

      <div className="mb-8">
        <div className="eyebrow text-violet-600">
          {isResubmit ? 'KYC resubmission' : 'KYC verification'}
        </div>
        <h1 className="font-display text-4xl font-bold mt-2">
          {isResubmit ? 'Resubmit your details' : 'Verify your identity'}
        </h1>
        <p className="text-ink-700 mt-3">
          KYC is required before commission payouts. Your documents are encrypted at rest
          and only accessible to authorised compliance reviewers.
        </p>
      </div>

      {isResubmit && (
        <Card className="mb-6 !border-danger/30 !bg-danger/5">
          <div className="eyebrow text-danger mb-2">Previous submission rejected</div>
          {previousRejectionReason ? (
            <p className="text-ink-800 text-sm">
              <span className="font-semibold">Reviewer note:</span>{' '}
              {previousRejectionReason}
            </p>
          ) : (
            <p className="text-ink-800 text-sm">
              Your previous submission was rejected. Please review your details and
              resubmit below.
            </p>
          )}
        </Card>
      )}

      <Card>
        <form onSubmit={handleSubmit} className="space-y-5">
          <FormField label="Full legal name" htmlFor="fullName">
            <Input
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="As shown on your document"
              required
            />
          </FormField>

          <FormField label="Date of birth" htmlFor="dateOfBirth">
            <Input
              id="dateOfBirth"
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              required
            />
          </FormField>

          <FormField label="Mobile number" htmlFor="mobileNumber">
            <Input
              id="mobileNumber"
              type="tel"
              value={mobileNumber}
              onChange={(e) => setMobileNumber(e.target.value)}
              placeholder="e.g. +91 98765 43210"
              required
              maxLength={25}
              autoComplete="tel"
            />
            <p className="text-xs text-ink-600 mt-1">
              Include your country code (e.g. <span className="font-mono">+91</span> for India,{' '}
              <span className="font-mono">+1</span> for US). We may contact you for verification.
            </p>
          </FormField>

          <FormField label="Country" htmlFor="countryCode">
            <select
              id="countryCode"
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-2xl border border-paper-300 bg-white text-ink focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none transition"
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name} ({c.code})
                </option>
              ))}
              <option value="OTHER">Other (specify below)</option>
            </select>
          </FormField>

          {isOtherCountry && (
            <FormField label="Specify country" htmlFor="countryOther">
              <Input
                id="countryOther"
                value={countryOther}
                onChange={(e) => setCountryOther(e.target.value)}
                placeholder="e.g. Pakistan, Sri Lanka, Bangladesh..."
                required
                maxLength={100}
              />
            </FormField>
          )}

          <FormField label="Document type" htmlFor="documentType">
            <select
              id="documentType"
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-2xl border border-paper-300 bg-white text-ink focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none transition"
            >
              {DOC_TYPES.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Document number" htmlFor="documentNumber">
            <Input
              id="documentNumber"
              value={documentNumber}
              onChange={(e) => setDocumentNumber(e.target.value)}
              placeholder="Number on your ID"
              required
            />
          </FormField>

          <div>
            <Label htmlFor="document">ID image (front)</Label>
            <input
              id="document"
              type="file"
              accept="image/jpeg,image/png"
              onChange={(e) => setDocumentFile(e.target.files?.[0] ?? null)}
              required
              className="block w-full text-sm text-ink-700 mt-1 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
            />
            <p className="text-xs text-ink-600 mt-1">
              JPEG or PNG, max 5 MB. Should be clear and readable.
            </p>
            {documentFile && (
              <p className="text-xs text-success mt-1">
                ✓ {documentFile.name} ({Math.round(documentFile.size / 1024)} KB)
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="selfie">Selfie holding your ID</Label>
            <input
              id="selfie"
              type="file"
              accept="image/jpeg,image/png"
              onChange={(e) => setSelfieFile(e.target.files?.[0] ?? null)}
              required
              className="block w-full text-sm text-ink-700 mt-1 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
            />
            <p className="text-xs text-ink-600 mt-1">
              Your face and the ID must both be clearly visible.
            </p>
            {selfieFile && (
              <p className="text-xs text-success mt-1">
                ✓ {selfieFile.name} ({Math.round(selfieFile.size / 1024)} KB)
              </p>
            )}
          </div>

          {/* ---------- Tax ID section ---------- */}
          <div className="pt-4 mt-2 border-t border-paper-200">
            <div className="eyebrow text-violet-600 mb-1">Tax information (optional)</div>
            <p className="text-sm text-ink-700 mb-4">
              For tax reporting in your jurisdiction. For example: PAN (India), SSN (USA),
              GST/VAT, TIN, or your country's equivalent. Leave blank if not applicable.
            </p>

            <FormField label="Tax ID number" htmlFor="taxIdNumber">
              <Input
                id="taxIdNumber"
                value={taxIdNumber}
                onChange={(e) => setTaxIdNumber(e.target.value)}
                placeholder="e.g. ABCDE1234F (PAN), 123-45-6789 (SSN)…"
                maxLength={64}
              />
            </FormField>

            <div className="mt-4">
              <Label htmlFor="taxDocument">Tax ID document (optional)</Label>
              <input
                id="taxDocument"
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                onChange={(e) => setTaxDocFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-ink-700 mt-1 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
              />
              <p className="text-xs text-ink-600 mt-1">
                JPEG, PNG, or PDF, max 5 MB. e.g. a scan of your PAN card.
              </p>
              {taxDocFile && (
                <p className="text-xs text-success mt-1">
                  ✓ {taxDocFile.name} ({Math.round(taxDocFile.size / 1024)} KB)
                </p>
              )}
            </div>
          </div>

          {error && (
            <div className="p-3 rounded-2xl bg-danger/5 border border-danger/20 text-danger text-sm">
              {error}
            </div>
          )}

          <Button type="submit" isLoading={submitting} className="w-full">
            Submit for review
          </Button>
        </form>
      </Card>

      <p className="text-xs text-ink-600 mt-6 text-center">
        By submitting, you confirm the information is accurate and you consent to its
        processing for identity verification purposes.
      </p>
    </div>
  );
}
